import { createContext, useContext, useState, useCallback } from 'react';

// ── Unit systems ───────────────────────────────────────────────────

export type UnitSystem = 'metric' | 'imperial';

// ── Dimension definitions ──────────────────────────────────────────

export type Dimension =
  | 'pressure'
  | 'flow'
  | 'temperature'
  /** A temperature difference (margins): scales but does not shift. */
  | 'temperature_delta'
  | 'length_mm'
  | 'length_m'
  | 'power';

interface UnitDef {
  metric: string;
  imperial: string;
  toImperial: (v: number) => number;
  toMetric: (v: number) => number;
}

const UNITS: Record<Dimension, UnitDef> = {
  pressure: {
    metric: 'kPa',
    imperial: 'psi',
    toImperial: (v) => v * 0.145038,
    toMetric: (v) => v / 0.145038,
  },
  flow: {
    metric: 'l/min',
    imperial: 'GPM',
    toImperial: (v) => v * 0.264172,
    toMetric: (v) => v / 0.264172,
  },
  temperature: {
    metric: '°C',
    imperial: '°F',
    toImperial: (v) => v * 9 / 5 + 32,
    toMetric: (v) => (v - 32) * 5 / 9,
  },
  temperature_delta: {
    metric: '°C',
    imperial: '°F',
    toImperial: (v) => v * 9 / 5,
    toMetric: (v) => v * 5 / 9,
  },
  length_mm: {
    metric: 'mm',
    imperial: 'in',
    toImperial: (v) => v * 0.0393701,
    toMetric: (v) => v / 0.0393701,
  },
  length_m: {
    metric: 'm',
    imperial: 'ft',
    toImperial: (v) => v * 3.28084,
    toMetric: (v) => v / 3.28084,
  },
  power: {
    metric: 'kW',
    imperial: 'HP',
    toImperial: (v) => v * 1.34102,
    toMetric: (v) => v / 1.34102,
  },
};

// ── Mapping from property keys to dimensions ───────────────────────

const PROPERTY_DIMENSION: Record<string, Dimension> = {
  flow_rate: 'flow',
  fixed_flow_lpm: 'flow',
  reference_flow_lpm: 'flow',
  inner_diameter_mm: 'length_mm',
  length_m: 'length_m',
  heat_input_kw: 'power',
  heat_rejection_kw: 'power',
  pressure_drop_mbar: 'pressure', // note: mbar is handled separately
};

// ── Public API ─────────────────────────────────────────────────────

export function getUnit(dimension: Dimension, system: UnitSystem): string {
  return UNITS[dimension][system];
}

export function getUnitForProperty(key: string, system: UnitSystem): string | null {
  const dim = PROPERTY_DIMENSION[key];
  if (!dim) return null;
  return getUnit(dim, system);
}

export function getDimension(key: string): Dimension | null {
  return PROPERTY_DIMENSION[key] ?? null;
}

/**
 * Convert a value from metric (internal) to the target unit system for display.
 */
export function convertForDisplay(value: number, dimension: Dimension, system: UnitSystem): number {
  if (system === 'metric') return value;
  return UNITS[dimension].toImperial(value);
}

/**
 * Convert a value from the current unit system back to metric (internal).
 */
export function convertToMetric(value: number, dimension: Dimension, system: UnitSystem): number {
  if (system === 'metric') return value;
  return UNITS[dimension].toMetric(value);
}

/**
 * Format a value for display with appropriate decimal places.
 */
export function formatValue(value: number, dimension: Dimension, system: UnitSystem, decimals?: number): string {
  const converted = convertForDisplay(value, dimension, system);
  const dp = decimals ?? (dimension === 'temperature' ? 1 : dimension === 'length_mm' ? 2 : 1);
  return converted.toFixed(dp);
}

/**
 * Format a value with its unit string.
 */
export function formatWithUnit(value: number, dimension: Dimension, system: UnitSystem, decimals?: number): string {
  return `${formatValue(value, dimension, system, decimals)} ${getUnit(dimension, system)}`;
}

// ── React Context ──────────────────────────────────────────────────

interface UnitContextValue {
  system: UnitSystem;
  toggle: () => void;
  unit: (dim: Dimension) => string;
  display: (value: number, dim: Dimension, decimals?: number) => string;
  displayWithUnit: (value: number, dim: Dimension, decimals?: number) => string;
  toMetric: (value: number, dim: Dimension) => number;
  toDisplay: (value: number, dim: Dimension) => number;
}

const UnitContext = createContext<UnitContextValue>({
  system: 'metric',
  toggle: () => {},
  unit: (dim) => getUnit(dim, 'metric'),
  display: (v) => v.toFixed(1),
  displayWithUnit: (v, dim) => `${v.toFixed(1)} ${getUnit(dim, 'metric')}`,
  toMetric: (v) => v,
  toDisplay: (v) => v,
});

export function useUnits(): UnitContextValue {
  return useContext(UnitContext);
}

export function useUnitSystem(): [UnitSystem, () => void, UnitContextValue] {
  const stored = localStorage.getItem('unit-system') as UnitSystem | null;
  const [system, setSystem] = useState<UnitSystem>(stored || 'metric');

  const toggle = useCallback(() => {
    setSystem((prev) => {
      const next = prev === 'metric' ? 'imperial' : 'metric';
      localStorage.setItem('unit-system', next);
      return next;
    });
  }, []);

  const contextValue: UnitContextValue = {
    system,
    toggle,
    unit: (dim) => getUnit(dim, system),
    display: (v, dim, dec) => formatValue(v, dim, system, dec),
    displayWithUnit: (v, dim, dec) => formatWithUnit(v, dim, system, dec),
    toMetric: (v, dim) => convertToMetric(v, dim, system),
    toDisplay: (v, dim) => convertForDisplay(v, dim, system),
  };

  return [system, toggle, contextValue];
}

export { UnitContext };
